import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Code, Brain, Zap, CheckCircle2, Award,
  Settings, Clock, TrendingUp, Shield, Database, Cpu
} from 'lucide-react';

const OpenAIGPTDevelopment = () => {
  const gptServices = [
    {
      icon: Code,
      title: 'Custom GPT Application Development',
      description: 'Build production-ready applications powered by GPT-4, GPT-4 Turbo, and GPT-3.5',
      deliverables: ['Custom API integrations', 'Full-stack web apps', 'Mobile applications', 'Chrome extensions']
    },
    {
      icon: Brain,
      title: 'Prompt Engineering & Optimization',
      description: 'Expert prompt design to maximize accuracy, reduce costs, and improve response quality',
      deliverables: ['Prompt templates', 'Chain-of-thought design', 'Few-shot learning', 'Cost optimization']
    },
    {
      icon: Database,
      title: 'RAG Implementation (Retrieval-Augmented Generation)',
      description: 'Connect GPT to your private data with vector databases for accurate, contextual responses',
      deliverables: ['Vector DB setup (Pinecone, Weaviate)', 'Document ingestion pipelines', 'Semantic search', 'Context management']
    },
    {
      icon: Settings,
      title: 'Fine-tuning & Model Customization',
      description: 'Fine-tune GPT models on your specific domain data for superior performance',
      deliverables: ['Training data preparation', 'Fine-tuning execution', 'Model evaluation', 'Deployment pipeline']
    },
    {
      icon: Zap,
      title: 'Function Calling & Tool Integration',
      description: 'Enable GPT to interact with external APIs, databases, and tools',
      deliverables: ['API integration', 'Function definitions', 'Error handling', 'Tool orchestration']
    },
    {
      icon: Shield,
      title: 'Enterprise Security & Compliance',
      description: 'Secure GPT implementations with data privacy, access control, and audit trails',
      deliverables: ['Azure OpenAI setup', 'SOC 2 compliance', 'Data encryption', 'Access policies']
    }
  ];

  const useCases = [
    {
      category: 'Customer Support',
      examples: ['AI chatbots with company knowledge', 'Ticket classification & routing', 'Automated email responses', 'Multilingual support'],
      roi: '70% reduction in support costs'
    },
    {
      category: 'Content Generation',
      examples: ['Blog post creation', 'Product descriptions', 'Marketing copy', 'SEO content'],
      roi: '10x faster content production'
    },
    {
      category: 'Data Analysis',
      examples: ['Natural language to SQL', 'Report generation', 'Data visualization', 'Insight extraction'],
      roi: '90% faster analysis'
    },
    {
      category: 'Internal Tools',
      examples: ['Code generation assistants', 'Documentation writers', 'Meeting summarizers', 'Knowledge bases'],
      roi: '40% productivity gain'
    },
    {
      category: 'Research & Analysis',
      examples: ['Document summarization', 'Literature review', 'Competitive analysis', 'Trend detection'],
      roi: '80% time savings'
    },
    {
      category: 'E-commerce',
      examples: ['Product recommendations', 'Virtual shopping assistants', 'Review analysis', 'Personalized marketing'],
      roi: '25% conversion increase'
    }
  ];

  const techStack = [
    {
      category: 'OpenAI Models',
      items: ['GPT-4', 'GPT-4 Turbo', 'GPT-3.5 Turbo', 'GPT-4o']
    },
    {
      category: 'Frameworks',
      items: ['LangChain', 'LlamaIndex', 'Semantic Kernel', 'Haystack']
    },
    {
      category: 'Vector Databases',
      items: ['Pinecone', 'Weaviate', 'Chroma', 'Qdrant']
    },
    {
      category: 'Deployment',
      items: ['Azure OpenAI', 'AWS Bedrock', 'Docker', 'Kubernetes']
    }
  ];

  const successStories = [
    {
      industry: 'SaaS Company',
      challenge: 'Needed AI chatbot to handle 10,000+ support tickets/month',
      solution: 'GPT-4 chatbot with RAG over documentation, integrated with Zendesk',
      results: ['70% ticket reduction', '95% accuracy', '2-second avg response', '4-week implementation'],
      technology: 'GPT-4 + Pinecone + LangChain'
    },
    {
      industry: 'E-commerce Platform',
      challenge: 'Required product description generation for 50,000+ SKUs',
      solution: 'GPT-3.5 Turbo fine-tuned on brand voice with automated pipeline',
      results: ['10x faster generation', '$200K annual savings', 'SEO-optimized content', '3-week delivery'],
      technology: 'GPT-3.5 Turbo Fine-tuning'
    },
    {
      industry: 'Legal Firm',
      challenge: 'Needed contract analysis tool for 1,000s of documents',
      solution: 'GPT-4 with function calling to extract key clauses and risks',
      results: ['80% faster review', '98% accuracy', 'Risk scoring', '6-week implementation'],
      technology: 'GPT-4 + Function Calling'
    }
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: 'OpenAI Partner Experience',
      description: 'Certified OpenAI developers with 50+ production GPT implementations'
    },
    {
      icon: Clock,
      title: 'Rapid Deployment',
      description: 'Production-ready GPT applications in 3-8 weeks, not months'
    },
    {
      icon: TrendingUp,
      title: 'Cost Optimization',
      description: 'Reduce OpenAI API costs by 40-60% through prompt optimization and caching'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Azure OpenAI, data privacy, SOC 2 compliance, and secure deployments'
    }
  ];

  return (
    <>
      <Helmet>
        <title>OpenAI GPT Development Services | Custom GPT-4 Applications | Agility</title>
        <meta
          name="description"
          content="Expert OpenAI GPT development services. Custom GPT-4 applications, RAG implementation, fine-tuning, prompt engineering. 50+ production implementations. 3-8 week delivery. Azure OpenAI, LangChain, vector databases."
        />
        <meta
          name="keywords"
          content="OpenAI GPT development, GPT-4 development, GPT-3.5 development, custom GPT applications, OpenAI API integration, RAG implementation, GPT fine-tuning, prompt engineering, LangChain development, Azure OpenAI"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/openai-gpt-development" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="OpenAI GPT Development Services | Custom GPT-4 Applications" />
        <meta property="og:description" content="Expert OpenAI GPT development. Custom GPT-4 applications, RAG, fine-tuning. 50+ production implementations. 3-8 week delivery." />
        <meta property="og:url" content="https://www.agilitytech.ai/openai-gpt-development" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />

        {/* Schema.org Service markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "OpenAI GPT Development Services",
            "description": "Custom GPT-4 application development, RAG implementation, fine-tuning, and prompt engineering services",
            "provider": {
              "@type": "Organization",
              "name": "Agility",
              "url": "https://www.agilitytech.ai"
            },
            "serviceType": "AI Development",
            "areaServed": "Worldwide"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-green-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full mb-8">
            <Brain className="w-4 h-4 text-green-400" />
            <span className="text-green-300 text-sm font-medium">OpenAI Certified Development Partner</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            <span className="text-teal-400">OpenAI GPT-4</span> Development Services
          </h1>

          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Build production-ready GPT-4 applications with RAG, fine-tuning, and enterprise security.
            50+ successful implementations. 3-8 week delivery. Expert prompt engineering and cost optimization.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Start Your GPT Project
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all"
            >
              View GPT Case Studies
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">50+</div>
              <div className="text-gray-400">GPT Apps Built</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">3-8</div>
              <div className="text-gray-400">Weeks Delivery</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">40-60%</div>
              <div className="text-gray-400">Cost Reduction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">99%</div>
              <div className="text-gray-400">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="text-teal-600">OpenAI GPT</span> Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end GPT development from concept to production deployment
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {gptServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-3">DELIVERABLES</div>
                    <ul className="space-y-2">
                      {service.deliverables.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              GPT-4 <span className="text-teal-600">Use Cases</span> & Applications
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your business with proven GPT implementations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="fade-in-up bg-white rounded-xl p-8 border border-gray-200 hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.category}</h3>
                <ul className="space-y-2 mb-6">
                  {useCase.examples.map((example, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{example}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-gray-200">
                  <div className="text-sm font-semibold text-gray-500 mb-1">TYPICAL ROI</div>
                  <div className="text-xl font-bold text-teal-600">{useCase.roi}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-teal-600">GPT Technology</span> Stack
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Best-in-class tools and frameworks for GPT development
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {techStack.map((category, index) => (
              <div key={index} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{category.category}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              GPT <span className="text-teal-600">Success Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real implementations delivering measurable business impact
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div key={index} className="fade-in-up bg-white rounded-xl p-8 border border-gray-200">
                <div className="text-sm font-semibold text-teal-600 mb-4">{story.industry}</div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">Challenge</h3>
                <p className="text-gray-700 mb-4">{story.challenge}</p>

                <h3 className="text-xl font-bold text-gray-900 mb-2">Solution</h3>
                <p className="text-gray-700 mb-4">{story.solution}</p>

                <h3 className="text-xl font-bold text-gray-900 mb-2">Results</h3>
                <ul className="space-y-2 mb-6">
                  {story.results.map((result, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{result}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-4 border-t border-gray-200">
                  <div className="text-sm font-semibold text-gray-500 mb-1">TECHNOLOGY</div>
                  <div className="text-gray-900 font-medium">{story.technology}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why Choose Agility for <span className="text-teal-400">OpenAI GPT</span> Development?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Proven expertise in building production-grade GPT applications
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="fade-in-up bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-teal-500/50 transition-all text-center">
                  <Icon className="w-12 h-12 text-teal-400 mb-6 mx-auto" />
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build Your GPT-4 Application?
          </h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            Get a custom GPT implementation plan and cost estimate in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Start Your GPT Project
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-teal-50">
            or call us at <span className="font-semibold">+91-7041402034</span>
          </p>
        </div>
      </section>
    </>
  );
};

export default OpenAIGPTDevelopment;
